import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { FloppyDisk, ArrowLeft } from "@phosphor-icons/react";
import ImageUploader from "../components/ImageUploader";
import { ageGroupFromDob } from "../lib/ageGroup";

const emptyPlayer = {
  first_name: "", last_name: "", nickname: "", jersey_number: "",
  position: "", secondary_position: "", preferred_foot: "Right",
  date_of_birth: "", height_cm: "", weight_kg: "", team: "", age_group: "",
  date_joined: "", photo_url: "",
  father_name: "", mother_name: "", guardian: "", phone: "", phone_secondary: "",
  email: "", address: "", emergency_contact: "",
  allergies: "", asthma: false, injuries: "", medications: "", doctor_notes: "",
};

const POSITIONS = ["Goalkeeper", "Defender", "Midfielder", "Forward", "Winger", "Striker"];
const FEET = ["Right", "Left", "Both"];
const inputCls = "w-full h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]";

// Auto-format date input to YYYY-MM-DD so mobile users can type it directly
function formatDateInput(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  const y = digits.slice(0, 4);
  const m = digits.slice(4, 6);
  const d = digits.slice(6, 8);
  if (digits.length <= 4) return y;
  if (digits.length <= 6) return `${y}-${m}`;
  return `${y}-${m}-${d}`;
}

function DateField({ value, onChange, testid }) {
  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      pattern="\d{4}-\d{2}-\d{2}"
      placeholder="YYYY-MM-DD"
      maxLength={10}
      data-testid={testid}
      className={inputCls}
      value={value || ""}
      onChange={(e) => onChange(formatDateInput(e.target.value))}
    />
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6">
      <div className="overline mb-4">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-neutral-500 font-semibold mb-1">{label}</div>
      {children}
    </label>
  );
}

export default function PlayerForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState(emptyPlayer);
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data || {}));
    if (isEdit) {
      api.get(`/players/${id}`).then((r) => {
        const p = r.data;
        setForm({ ...emptyPlayer, ...p, jersey_number: p.jersey_number ?? "", height_cm: p.height_cm ?? "", weight_kg: p.weight_kg ?? "" });
      });
    }
  }, [id, isEdit]);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      jersey_number: form.jersey_number === "" ? null : Number(form.jersey_number),
      height_cm: form.height_cm === "" ? null : Number(form.height_cm),
      weight_kg: form.weight_kg === "" ? null : Number(form.weight_kg),
    };
    try {
      if (isEdit) {
        await api.put(`/players/${id}`, payload);
        toast.success("Player updated");
        navigate(`/players/${id}`);
      } else {
        const r = await api.post("/players", payload);
        toast.success("Player added");
        navigate(`/players/${r.data.id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={save} className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">{isEdit ? "Edit" : "New"}</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tight mt-1">{isEdit ? "Edit Player" : "Add Player"}</h1>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="h-12 px-4 rounded-md border border-neutral-800 hover:bg-neutral-900 flex items-center gap-2"
          >
            <ArrowLeft size={18} weight="bold" /> Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="save-player-btn"
            className="h-12 px-6 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider flex items-center gap-2 disabled:opacity-60"
          >
            <FloppyDisk size={18} weight="bold" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <Section title="Personal">
        <Field label="First Name *"><input required data-testid="pf-first-name" className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} /></Field>
        <Field label="Last Name *"><input required data-testid="pf-last-name" className={inputCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} /></Field>
        <Field label="Nickname"><input className={inputCls} value={form.nickname} onChange={(e) => set("nickname", e.target.value)} /></Field>
        <Field label="Jersey Number"><input type="number" data-testid="pf-jersey" className={inputCls} value={form.jersey_number} onChange={(e) => set("jersey_number", e.target.value)} /></Field>
        <Field label="Position">
          <select className={inputCls} value={form.position} onChange={(e) => set("position", e.target.value)}>
            <option value="">—</option>
            {POSITIONS.map((p) => <option key={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Secondary Position">
          <select className={inputCls} value={form.secondary_position} onChange={(e) => set("secondary_position", e.target.value)}>
            <option value="">—</option>
            {POSITIONS.map((p) => <option key={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Preferred Foot">
          <select className={inputCls} value={form.preferred_foot} onChange={(e) => set("preferred_foot", e.target.value)}>
            {FEET.map((f) => <option key={f}>{f}</option>)}
          </select>
        </Field>
        <Field label="Date of Birth"><DateField testid="pf-dob" value={form.date_of_birth} onChange={(v) => set("date_of_birth", v)} /></Field>
        <Field label="Height (cm)"><input type="number" className={inputCls} value={form.height_cm} onChange={(e) => set("height_cm", e.target.value)} /></Field>
        <Field label="Weight (kg)"><input type="number" className={inputCls} value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} /></Field>
        <Field label="Team">
          <select className={inputCls} value={form.team} onChange={(e) => set("team", e.target.value)}>
            <option value="">—</option>
            {(settings.teams || []).map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Age Group (auto from DOB)">
          <div data-testid="pf-age-group-auto" className={`${inputCls} flex items-center font-bold text-[#FFD166]`}>
            {ageGroupFromDob(form.date_of_birth) || form.age_group || "Enter date of birth"}
          </div>
        </Field>
        <Field label="Date Joined"><DateField testid="pf-date-joined" value={form.date_joined} onChange={(v) => set("date_joined", v)} /></Field>
        <div className="md:col-span-2">
          <div className="text-xs text-neutral-500 font-semibold mb-2">Player Photo</div>
          <ImageUploader
            value={form.photo_url}
            onChange={(url) => set("photo_url", url)}
            kind="player"
            shape="square"
            size={96}
            testId="player-photo-uploader"
          />
        </div>
      </Section>

      <Section title="Parent / Guardian">
        <Field label="Father Name"><input className={inputCls} value={form.father_name} onChange={(e) => set("father_name", e.target.value)} /></Field>
        <Field label="Mother Name"><input className={inputCls} value={form.mother_name} onChange={(e) => set("mother_name", e.target.value)} /></Field>
        <Field label="Guardian"><input className={inputCls} value={form.guardian} onChange={(e) => set("guardian", e.target.value)} /></Field>
        <Field label="Phone"><input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
        <Field label="Secondary Phone"><input className={inputCls} value={form.phone_secondary} onChange={(e) => set("phone_secondary", e.target.value)} /></Field>
        <Field label="Email"><input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
        <Field label="Address"><input className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
        <Field label="Emergency Contact"><input className={inputCls} value={form.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} /></Field>
      </Section>

      <Section title="Medical">
        <Field label="Allergies"><input className={inputCls} value={form.allergies} onChange={(e) => set("allergies", e.target.value)} /></Field>
        <Field label="Asthma">
          <select className={inputCls} value={String(form.asthma)} onChange={(e) => set("asthma", e.target.value === "true")}>
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </Field>
        <Field label="Injuries"><input className={inputCls} value={form.injuries} onChange={(e) => set("injuries", e.target.value)} /></Field>
        <Field label="Medications"><input className={inputCls} value={form.medications} onChange={(e) => set("medications", e.target.value)} /></Field>
        <div className="md:col-span-2">
          <Field label="Doctor Notes">
            <textarea rows={3} className="w-full p-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]" value={form.doctor_notes} onChange={(e) => set("doctor_notes", e.target.value)} />
          </Field>
        </div>
      </Section>
    </form>
  );
}
